import React, { useEffect, useRef, useState } from 'react';
import '../styles/about.css';
import {
  fetchComments,
  formatCommentDate,
  getAvatarInitials,
  submitComment,
} from '../services/commentsService';

const presenceServices = [
  {
    id: 'intervencion',
    title: 'Intervención y Apoyo Educativo',
    image: 'about-service-intervencion.png',
  },
  {
    id: 'adecuaciones',
    title: 'Adecuaciones Curriculares',
    image: 'about-service-adecuaciones.png',
  },
  {
    id: 'tutorias',
    title: 'Tutorías y Técnicas de Estudio',
    image: 'about-service-tutorias.png',
  },
];

const CalendarIcon = () => (
  <svg className="about-service-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M8 3v4M16 3v4M4 10h16" />
  </svg>
);

const LocationIcon = () => (
  <svg className="about-service-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 21s7-4.5 7-11a7 7 0 10-14 0c0 6.5 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

const AboutPage = () => {
  const servicesListRef = useRef(null);
  const [comments, setComments] = useState([]);
  const [visibleCount, setVisibleCount] = useState(3);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    setLoadError('');
    fetchComments('about')
      .then(setComments)
      .catch((error) => {
        setComments([]);
        setLoadError(error.message || 'No se pudieron cargar los comentarios.');
      });
  }, []);

  const scrollServices = (direction) => {
    if (!servicesListRef.current) {
      return;
    }

    servicesListRef.current.scrollBy({
      top: direction * 240,
      behavior: 'smooth',
    });
  };

  const handleSubmitComment = (event) => {
    event.preventDefault();
    setFormMessage('');
    setIsSubmitting(true);

    submitComment({
      pagina: 'about',
      nombre_usuario: 'Visitante',
      texto: commentText,
    })
      .then(() => {
        setCommentText('');
        setFormMessage('Tu comentario fue enviado y quedará visible cuando sea aprobado.');
        return fetchComments('about');
      })
      .then(setComments)
      .catch((error) => {
        setFormMessage(error.message || 'No se pudo enviar el comentario.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const visibleComments = comments.filter((item) => item.estado === 'aprobado').slice(0, visibleCount);
  const approvedCount = comments.filter((item) => item.estado === 'aprobado').length;

  return (
    <div className="about-root">
      <section className="about-section about-section-action">
        <div className="about-action-card">
          <div className="about-action-copy">
            <h1 className="about-action-title">
              Cada Acción que Emprendemos para Fortalecer a Familias, Educadores y Niños.
            </h1>
            <p className="about-action-text">
              Fomentando su autonomía y fortaleciendo su capacidad para abogar por sus derechos.{' '}
              <strong>
                Trabajamos incansablemente para construir un futuro donde cada individuo, sin importar sus
                capacidades,
              </strong>{' '}
              pueda vivir con dignidad, confianza y plena independencia.
            </p>
          </div>

          <div className="about-action-visual">
            <img
              src={`${process.env.PUBLIC_URL}/images/about-cada-accion.png`}
              alt="Voluntarios uniendo sus manos en equipo"
              className="about-action-photo"
            />
          </div>
        </div>
      </section>

      <section className="about-section about-section-main">
        <div className="about-main-inner">
          <div className="about-info">
            <h2 className="about-info-title">Acerca de Nosotros</h2>

            <p className="about-info-text">
              Creemos en el potencial ilimitado de cada niño y en la importancia de brindar a las familias y
              educadores el apoyo necesario para que puedan acompañar cada etapa del desarrollo de sus hijos. Somos
              una fundación integral dedicada a conectar a las familias con recursos, herramientas y una comunidad
              comprometida con la inclusión y la neuro diversidad.
            </p>

            <p className="about-info-text">
              Desde nuestros inicios, nuestra misión ha sido clara: construir un entorno inclusivo y comprensivo que
              valore y celebre la diversidad en todas sus formas. Imagina un espacio donde las familias encuentran
              desde orientación especializada hasta programas de empoderamiento, talleres educativos y redes de apoyo
              diseñadas para fomentar la aceptación y el respeto hacia las capacidades únicas de cada niño.
            </p>

            <p className="about-info-text">
              Nuestra fundación no solo está dedicada a las familias; también somos un puente para educadores,
              profesionales y aliados que desean contribuir a una sociedad más inclusiva. Con opciones de
              colaboración accesibles y flexibles, tanto familias como instituciones pueden unirse a nuestra comunidad
              mediante programas adaptados a sus necesidades.
            </p>

            <h3 className="about-info-subtitle">¿Qué nos hace únicos?</h3>

            <ul className="about-info-list">
              <li>
                <strong>Entorno inclusivo:</strong> Trabajamos en colaboración con familias, educadores y la comunidad
                para promover la conciencia y la aceptación de la neurodiversidad.
              </li>
              <li>
                <strong>Empoderamiento:</strong> Brindamos herramientas y apoyo para que familias y educadores se
                conviertan en defensores efectivos de los derechos y necesidades de los niños neurodivergentes.
              </li>
              <li>
                <strong>Educación y sensibilización:</strong> Promovemos programas de sensibilización para superar
                estigmas y barreras, construyendo una sociedad donde todos los niños tengan igualdad de oportunidades.
              </li>
              <li>
                <strong>Comunidad confiable:</strong> Fomentamos un espacio de apoyo mutuo donde las familias pueden
                compartir experiencias, recursos y consejos, siempre guiados por la compasión y el respeto.
              </li>
              <li>
                <strong>Acceso integral:</strong> Ofrecemos recursos tanto en formato digital como presencial,
                asegurando que nuestro apoyo esté al alcance de todos.
              </li>
            </ul>

            <p className="about-info-text">
              En <strong>Beyond Limits Foundation</strong>, celebramos las fortalezas únicas de cada niño y la
              dedicación de quienes los acompañan. Desde encontrar el apoyo adecuado hasta participar en talleres que
              enciendan la confianza y autonomía de tu hijo, estamos aquí para ayudarte en cada paso del camino.
            </p>

            <p className="about-info-text">
              Somos más que una fundación: somos un faro de esperanza y apoyo, dedicados a hacer del crecimiento
              infantil una journey llena de oportunidades, aprendizajes y momentos significativos. Bienvenidos a
              Beyond Limits, donde trabajamos para que cada niño florezca y sea valorado en toda su singularidad.
            </p>
          </div>

          <aside className="about-services">
            <div className="about-services-header">
              <h2 className="about-services-title">Servicios Presenciales</h2>
              <div className="about-services-nav">
                <button
                  type="button"
                  className="about-services-nav-btn"
                  aria-label="Ver servicios anteriores"
                  onClick={() => scrollServices(-1)}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="about-services-nav-btn"
                  aria-label="Ver más servicios"
                  onClick={() => scrollServices(1)}
                >
                  ›
                </button>
              </div>
            </div>

            <div className="about-services-list" ref={servicesListRef}>
              {presenceServices.map((service) => (
                <article key={service.id} className="about-service-card">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/${service.image}`}
                    alt=""
                    className="about-service-photo"
                    aria-hidden="true"
                  />
                  <div className="about-service-body">
                    <h3 className="about-service-name">{service.title}</h3>
                    <p className="about-service-meta">
                      <CalendarIcon />
                      Lunes a sábado
                    </p>
                    <p className="about-service-meta">
                      <LocationIcon />
                      Avenida Central 456, Edificio Deportivo, David, Chiriquí
                    </p>
                    <p className="about-service-price">
                      Precio: <strong>$300</strong> / mes
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <section className="about-section about-section-comments">
        <div className="about-comments-card">
          <div className="about-comments-inner">
            <h2 className="about-comments-title">Deja tu Comentario</h2>

            <form className="about-comment-form" onSubmit={handleSubmitComment}>
              <textarea
                className="about-comment-input"
                placeholder="Añadir comentario"
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
                rows={5}
              />
              <div className="about-comment-form-actions">
                <button type="submit" className="about-comment-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Enviando...' : 'Enviar >'}
                </button>
              </div>
            </form>

            {formMessage ? <p className="about-comment-form-message">{formMessage}</p> : null}
            {loadError ? <p className="about-comment-form-message about-comment-form-message--error">{loadError}</p> : null}

            <div className="about-comments-list">
              {visibleComments.map((comment) => (
                <article key={comment.id} className="about-comment-card">
                  <div
                    className="about-comment-avatar"
                    aria-hidden="true"
                    style={
                      comment.avatar_url
                        ? { backgroundImage: `url(${comment.avatar_url})` }
                        : undefined
                    }
                  >
                    {!comment.avatar_url ? getAvatarInitials(comment.nombre_usuario) : null}
                  </div>
                  <div className="about-comment-content">
                    <div className="about-comment-meta">
                      <p className="about-comment-author">{comment.nombre_usuario}</p>
                      <p className="about-comment-date">{formatCommentDate(comment.creado_en)}</p>
                    </div>
                    <p className="about-comment-text">&ldquo;{comment.texto}&rdquo;</p>
                  </div>
                </article>
              ))}
            </div>

            {approvedCount > visibleCount ? (
              <button
                type="button"
                className="about-comments-more"
                onClick={() => setVisibleCount((count) => count + 3)}
              >
                Ver más comentarios &gt;
              </button>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
